import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * MessageBubble - Individual message display component
 *
 * @param {Object} props
 * @param {Object} props.message - Message object with role, content, timestamp
 * @param {boolean} props.showTimestamp - Whether to show timestamp (default: true)
 */
const MessageBubble = ({ message, showTimestamp = true }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  // Format timestamp
  const getFormattedTime = () => {
    if (!message.timestamp) return '';

    try {
      const date = new Date(message.timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (err) {
      return '';
    }
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start max-w-[80%]`}>
        {/* Avatar/Icon */}
        <div className={`flex-shrink-0 ${isUser ? 'ml-3' : 'mr-3'}`}>
          {isAssistant && (
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            </div>
          )}
          {isUser && (
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Message Content */}
        <div className="flex flex-col min-w-0">
          <div
            className={`
              px-4 py-2 rounded-lg min-w-0
              ${isUser
                ? 'bg-blue-600 text-white rounded-br-none'
                : 'bg-gray-100 text-gray-900 rounded-bl-none'
              }
            `}
          >
            <div className="text-sm markdown-content" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Code blocks with syntax highlighting
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                      <SyntaxHighlighter
                        style={vscDarkPlus}
                        language={match[1]}
                        PreTag="div"
                        className="rounded-md my-2 text-xs"
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code
                        className={`${
                          isUser ? 'bg-blue-700' : 'bg-gray-200'
                        } px-1.5 py-0.5 rounded text-xs font-mono`}
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  },
                  // Paragraphs
                  p({ children }) {
                    return <p className="mb-2 last:mb-0">{children}</p>;
                  },
                  // Headings
                  h1({ children }) {
                    return <h1 className="text-lg font-bold mb-2 mt-3 first:mt-0">{children}</h1>;
                  },
                  h2({ children }) {
                    return <h2 className="text-base font-bold mb-2 mt-3 first:mt-0">{children}</h2>;
                  },
                  h3({ children }) {
                    return <h3 className="text-sm font-bold mb-1 mt-2 first:mt-0">{children}</h3>;
                  },
                  // Lists
                  ul({ children }) {
                    return <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>;
                  },
                  ol({ children }) {
                    return <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>;
                  },
                  li({ children }) {
                    return <li className="ml-2">{children}</li>;
                  },
                  // Blockquotes
                  blockquote({ children }) {
                    return (
                      <blockquote
                        className={`border-l-4 pl-3 py-1 my-2 italic ${
                          isUser ? 'border-blue-400' : 'border-gray-400'
                        }`}
                      >
                        {children}
                      </blockquote>
                    );
                  },
                  // Links
                  a({ children, href }) {
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`underline ${
                          isUser ? 'text-blue-100 hover:text-white' : 'text-blue-600 hover:text-blue-800'
                        }`}
                      >
                        {children}
                      </a>
                    );
                  },
                  // Tables
                  table({ children }) {
                    return (
                      <table className="border-collapse border border-gray-300 my-2 text-xs">
                        {children}
                      </table>
                    );
                  },
                  th({ children }) {
                    return (
                      <th className="border border-gray-300 px-2 py-1 bg-gray-200 font-semibold">
                        {children}
                      </th>
                    );
                  },
                  td({ children }) {
                    return <td className="border border-gray-300 px-2 py-1">{children}</td>;
                  },
                  // Horizontal rule
                  hr() {
                    return <hr className={`my-3 ${isUser ? 'border-blue-400' : 'border-gray-300'}`} />;
                  },
                  // Strong/Bold
                  strong({ children }) {
                    return <strong className="font-bold">{children}</strong>;
                  },
                  // Emphasis/Italic
                  em({ children }) {
                    return <em className="italic">{children}</em>;
                  },
                }}
              >
                {message.content}
              </ReactMarkdown>
              {message.isStreaming && (
                <span className="inline-block w-2 h-4 ml-1 bg-gray-700 animate-pulse" />
              )}
            </div>
          </div>

          {/* Timestamp */}
          {showTimestamp && (
            <span className={`text-xs text-gray-500 mt-1 ${isUser ? 'text-right' : 'text-left'}`}>
              {getFormattedTime()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
